import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Button,
    Table,
    Dropdown,
} from 'antd';
import { AiOutlineEllipsis } from 'react-icons/ai';
import { MenuDriver } from './Menu';
import { checkDriverStatus } from '../../utils/driver.util';
import { IDriver } from '../../interfaces/driver.interface';

interface DriversTableProps {
    drivers: IDriver[];
    setPagination: (pagination: { limit: number; page: number; }) => void;
    pagination: { limit: number; page: number; };
    total: number;
    loading: boolean;
    columnsSelected?: string[];
}

const DriversTable: FC<DriversTableProps> = ({
    drivers,
    setPagination,
    pagination,
    total,
    loading,
    columnsSelected,
}) => {
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const columns = [
        {
            title: td('FULL_NAME'),
            dataIndex: 'name',
            key: 'name',
            align: 'center' as 'center',
            render: (name: string, driver: any) => `${name} ${driver.lastName}`,
        },
        {
            title: td('EMAIL'),
            dataIndex: 'email',
            key: 'email',
            align: 'center' as 'center',
        },
        {
            title: td('PHONE'),
            dataIndex: 'fullMobile',
            key: 'fullMobile',
            align: 'center' as 'center',
            render: (fullMobile: string, driver: any) => (
                fullMobile
                || `${driver.countryCode} ${driver.mobile}`
                || td('NOT_AVAILABLE')
            ),
        },
        {
            title: td('STATUS'),
            dataIndex: 'status',
            key: 'status',
            align: 'center' as 'center',
            render: (_: string, driver: IDriver) => {
                const { isBusy, onDutyFlag } = driver;
                const status = checkDriverStatus(isBusy, onDutyFlag);
                return (
                    <div className={`table-status ${status}`}>
                        {td(status)}
                    </div>
                );
            },
        },
        {
            title: td('ACTIONS'),
            dataIndex: '_id',
            key: '_id',
            align: 'center' as 'center',
            render: (_id: string, driver: IDriver) => (
                <Dropdown
                    overlay={(
                        <MenuDriver driver={driver} />
                    )}
                    trigger={['click']}
                    // @ts-ignore
                    onClick={(e: any) => { e.stopPropagation(); }}
                >
                    <Button ghost icon={<AiOutlineEllipsis size={24} style={{ color: 'black' }} />} />
                </Dropdown>
            ),
        },
    ];

    const visibleColumns = columnsSelected
        ? columns.filter((col) => col.key === '_id' || columnsSelected.includes(col.key))
        : columns;

    const onChange = (page: number, pageSize: number) => {
        setPagination({
            limit: pageSize,
            page,
        });
    };

    return (
        <Table
            className="table"
            dataSource={drivers}
            columns={visibleColumns}
            scroll={{ x: 1000 }}
            locale={{
                filterConfirm: 'Ok',
                filterReset: 'Reset',
                emptyText: td('NO_DATA'),
            }}
            pagination={{
                total,
                pageSize: pagination.limit,
                current: pagination.page,
                onChange,
            }}
            rowKey="_id"
            loading={loading}
        />
    );
};

export { DriversTable };
